import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, resetPassword, confirmResetPassword, getCurrentUser, signOut } from 'aws-amplify/auth';
import { logEvent } from '../api';

function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e53e3e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PasswordChecklist({ password }) {
  const checks = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Contains uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter (a-z)', met: /[a-z]/.test(password) },
    { label: 'Contains a number (0-9)', met: /[0-9]/.test(password) },
    { label: 'Contains a special character (!@#$%^&*)', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) },
  ];

  return (
    <div style={{
      backgroundColor: '#f7fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      padding: '12px',
      marginBottom: '16px'
    }}>
      {checks.map((check, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: index < checks.length - 1 ? '6px' : '0'
        }}>
          {check.met ? <CheckIcon /> : <XIcon />}
          <span style={{
            fontSize: '12px',
            color: check.met ? '#38a169' : '#e53e3e'
          }}>
            {check.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(false);
  const [resetCodeStep, setResetCodeStep] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);
  const [showNewPasswordChecklist, setShowNewPasswordChecklist] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const clearStaleSession = async () => {
      try {
        await getCurrentUser();
        await signOut();
      } catch {
        // No existing session
      }
      setCheckingSession(false);
    };
    clearStaleSession();
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await signIn({ username: email, password: password });
      await logEvent('login');
      navigate('/appeal');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError('');
    try {
      await resetPassword({ username: email });
      setResetCodeStep(true);
      setForgotPasswordStep(false);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleConfirmReset = async () => {
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: resetCode,
        newPassword: newPassword,
      });
      setResetCodeStep(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setResetCode('');
      setSuccessMessage('Password reset successfully. Please log in.');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (checkingSession) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f0f4f8'
      }} />
    );
  }

  if (forgotPasswordStep) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f0f4f8'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          width: '400px'
        }}>
          <h1 style={{ color: '#1a365d', textAlign: 'center', marginBottom: '8px' }}>Clinix AI</h1>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
            Enter your email to reset your password
          </p>
          {error && (
            <p style={{
              color: '#e53e3e',
              backgroundColor: '#fff5f5',
              padding: '10px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </p>
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleForgotPassword(); }}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: '24px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
          <button
            onClick={handleForgotPassword}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#a0aec0' : '#1a365d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '16px'
            }}
          >
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
          <p style={{ textAlign: 'center', color: '#666' }}>
            <span
              onClick={() => { setForgotPasswordStep(false); setError(''); }}
              style={{ color: '#1a365d', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Back to Login
            </span>
          </p>
        </div>
      </div>
    );
  }

  if (resetCodeStep) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f0f4f8'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          width: '400px'
        }}>
          <h1 style={{ color: '#1a365d', textAlign: 'center', marginBottom: '8px' }}>Clinix AI</h1>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
            Enter the code sent to your email and your new password
          </p>
          {error && (
            <p style={{
              color: '#e53e3e',
              backgroundColor: '#fff5f5',
              padding: '10px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </p>
          )}
          <input
            type="text"
            autoComplete="one-time-code"
            placeholder="Reset code"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmReset(); }}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: '16px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onFocus={() => setShowNewPasswordChecklist(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmReset(); }}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '45px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <span
              onClick={() => setShowNewPassword(!showNewPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
            </span>
          </div>

          {showNewPasswordChecklist && <PasswordChecklist password={newPassword} />}

          <div style={{ position: 'relative', marginBottom: '24px' }}>
            <input
              type={showConfirmNewPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              autoComplete="new-password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmReset(); }}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '45px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <span
              onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {showConfirmNewPassword ? <EyeOffIcon /> : <EyeIcon />}
            </span>
          </div>

          <button
            onClick={handleConfirmReset}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#a0aec0' : '#1a365d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f0f4f8'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        width: '400px'
      }}>
        <h1 style={{ color: '#1a365d', textAlign: 'center', marginBottom: '8px' }}>
          Clinix AI
        </h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          PA Denial Appeal Generator
        </p>

        {error && (
          <p style={{
            color: '#e53e3e',
            backgroundColor: '#fff5f5',
            padding: '10px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </p>
        )}

        {successMessage && (
          <p style={{
            color: '#38a169',
            backgroundColor: '#f0fff4',
            padding: '10px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {successMessage}
          </p>
        )}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
          style={{
            width: '100%',
            padding: '12px',
            marginBottom: '16px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '16px',
            boxSizing: 'border-box'
          }}
        />

        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: '45px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
          <span
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </span>
        </div>

        <p style={{ textAlign: 'right', marginBottom: '20px' }}>
          <span
            onClick={() => { setForgotPasswordStep(true); setError(''); setSuccessMessage(''); }}
            style={{ color: '#1a365d', cursor: 'pointer', fontSize: '14px' }}
          >
            Forgot password?
          </span>
        </p>

        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: loading ? '#a0aec0' : '#1a365d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>

        <p style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
          Don't have an account?{' '}
          <span
            onClick={() => navigate('/signup')}
            style={{ color: '#1a365d', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Sign Up
          </span>
        </p>
      </div>
    </div>
  );
}

export default Login;